import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, FileSignature, Download, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SEA_EMPTY, SeaFields, downloadContractPdf, toSeaPayload, validateSea } from "@/components/SeaForm";

function TemplateStatus({ info }) {
  if (info.template) {
    return (
      <div className="flex items-start gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3" data-testid="sea-template-match">
        <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Template selected automatically: <b>{info.template.owner_name}</b> ({info.template.file_name})</span>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="sea-template-missing">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>No SEA template exists for owner <b>{info.owner_name}</b>. Ask Super Admin to upload one under Settings › SEA Templates before generating.</span>
    </div>
  );
}

function Result({ contract, onReset }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try { await downloadContractPdf(api, contract); } catch { toast.error("Could not download the PDF"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4" data-testid="sea-result">
      <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 className="w-5 h-5" /> SEA generated</div>
      <div className="text-sm text-slate-600">
        {contract.seafarer_name} · {contract.rank} · {contract.vessel_name}. Signed by {contract.signed_by_name}
        {contract.signature_used ? "" : " (no signature on file — a blank “(Signature)” space was left)"}.
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={download} disabled={busy} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="sea-download-pdf-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" /> Download PDF</>}
        </Button>
        <Button variant="outline" onClick={() => toast.info("Sending to seafarer is coming soon")} data-testid="sea-send-btn">
          <Send className="w-4 h-4 mr-1" /> Send to Seafarer
        </Button>
        <Button variant="ghost" onClick={onReset} data-testid="sea-generate-another-btn">Generate another</Button>
        <Link to="/sea-contracts" className="text-sm text-cyan-700 hover:underline self-center ml-1" data-testid="sea-view-all-link">View all contracts</Link>
      </div>
    </div>
  );
}

export function GenerateSea({ vesselId }) {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(SEA_EMPTY);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/vessels/${vesselId}/sea-form`).then(({ data }) => setInfo(data)).catch((err) => toast.error(formatApiError(err)));
  }, [vesselId]);

  const submit = async (e) => {
    e.preventDefault();
    const err = validateSea(form);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/vessels/${vesselId}/sea-contracts`, toSeaPayload(form));
      setResult(data);
      toast.success("SEA contract generated");
    } catch (er) {
      toast.error(formatApiError(er));
    } finally {
      setSaving(false);
    }
  };

  if (!info) return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>;
  if (result) return <Result contract={result} onReset={() => { setResult(null); setForm(SEA_EMPTY); }} />;

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="generate-sea-form">
      <TemplateStatus info={info} />
      {!info.has_signature && (
        <div className="text-xs text-slate-500" data-testid="sea-no-signature-note">You have no signature image on file, so a blank “(Signature)” space will be left on the contract.</div>
      )}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <SeaFields form={form} setForm={setForm} ranks={info.ranks} vesselName={info.vessel_name} ownerName={info.owner_name} />
      </div>
      {!info.can_generate && <div className="text-xs text-amber-700" data-testid="sea-generate-disabled-note">SEA generation is not enabled for you. Super Admin can switch it on in User Management.</div>}
      <Button type="submit" disabled={saving || !info.template || !info.can_generate} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="sea-generate-btn">
        {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating PDF…</> : <><FileSignature className="w-4 h-4 mr-1" /> Generate SEA</>}
      </Button>
    </form>
  );
}
